import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { timeAgo } from "@/lib/format";
import { Sparkle, Smiley, Warning, TrendUp, ChatText, Star, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const RANGES = [{ v: 7, l: "Last 7 days" }, { v: 30, l: "Last 30 days" }, { v: 90, l: "Last 90 days" }];
const sevColor = { high: "text-destructive bg-destructive/10 border-destructive/20", medium: "text-amber-600 bg-amber-50 border-amber-200", low: "text-muted-foreground bg-secondary border-border" };
const scoreColor = (s) => (s <= 2 ? "text-destructive" : s === 3 ? "text-amber-600" : "text-success");
const chanColor = { survey: "bg-primary/10 text-primary", email: "bg-blue-50 text-blue-600", sms: "bg-violet-50 text-violet-600" };

export default function Surveys() {
  const [days, setDays] = useState(30);
  const [analysis, setAnalysis] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["surveys", days], queryFn: async () => (await api.get("/surveys", { params: { days } })).data });

  const analyze = useMutation({
    mutationFn: async () => (await api.post("/surveys/analyze", { days })).data,
    onSuccess: (d) => { setAnalysis(d); toast.success("Claude analyzed your feedback ✨"); },
    onError: () => toast.error("Analysis failed"),
  });

  const rangeLabel = RANGES.find((r) => r.v === days)?.l || `Last ${days} days`;

  return (
    <Page>
      <PageHeader title="Surveys" subtitle="Guest feedback with AI analysis for any time period.">
        <Select value={String(days)} onValueChange={(v) => { setDays(Number(v)); setAnalysis(null); }}>
          <SelectTrigger className="w-[160px] h-10" data-testid="survey-range"><SelectValue /></SelectTrigger>
          <SelectContent>{RANGES.map((r) => <SelectItem key={r.v} value={String(r.v)}>{r.l}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat icon={ChatText} label="Responses" value={data?.count ?? 0} hint={rangeLabel} accent />
        <Stat icon={Star} label="Avg score" value={`${data?.avg ?? 0}`} hint="out of 5" />
        <Stat icon={ThumbsUp} label="Promoters (4–5)" value={data?.promoters ?? 0} hint={`${data?.csat ?? 0}% CSAT`} />
        <Stat icon={ThumbsDown} label="Detractors (1–2)" value={data?.detractors ?? 0} hint="Need attention" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-card p-5 fade-up">
          <h3 className="font-heading font-semibold mb-4">Average score trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data?.trend || []} margin={{ left: -22, right: 6, top: 6 }}>
              <defs><linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#EA580C" stopOpacity={0.3} /><stop offset="100%" stopColor="#EA580C" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: "#888" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
              <Area type="monotone" dataKey="avg" stroke="#EA580C" strokeWidth={2} fill="url(#sg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-lg border border-border bg-card p-5 fade-up" style={{ animationDelay: "80ms" }}>
          <h3 className="font-heading font-semibold mb-3">Score distribution</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((s) => {
              const c = data?.distribution?.[s] ?? 0;
              const pct = data?.count ? Math.round((c / data.count) * 100) : 0;
              return (
                <div key={s} className="flex items-center gap-2 text-sm">
                  <span className="w-8 flex items-center gap-0.5 text-amber-500">{s}<Star size={12} weight="fill" /></span>
                  <div className="flex-1 h-2.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} /></div>
                  <span className="w-8 text-right text-muted-foreground text-xs tabular-nums">{c}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* AI analysis */}
      <div className="rounded-lg border border-primary/20 bg-primary/[0.03] p-5 mt-4 fade-up">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-md bg-primary/10 text-primary flex items-center justify-center"><Sparkle size={18} weight="fill" /></span>
            <div>
              <h3 className="font-heading font-semibold">AI feedback summary</h3>
              <p className="text-xs text-muted-foreground">Claude reads every comment in {rangeLabel.toLowerCase()} and surfaces themes.</p>
            </div>
          </div>
          <Button data-testid="analyze-surveys-btn" className="gap-1.5" disabled={analyze.isPending || !data?.count} onClick={() => analyze.mutate()}>
            {analyze.isPending ? "Analyzing…" : <><Sparkle size={15} weight="fill" /> {analysis ? "Re-analyze" : "Analyze feedback"}</>}
          </Button>
        </div>

        {analysis && (
          <div className="mt-4 space-y-4" data-testid="survey-analysis">
            <p className="text-sm leading-relaxed bg-card border border-border rounded-md p-3.5">{analysis.summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-md border border-border bg-card p-4">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-destructive mb-2.5"><Warning size={16} weight="fill" /> Common issues</div>
                <div className="space-y-2">
                  {analysis.issues.length === 0 && <p className="text-xs text-muted-foreground">No notable issues 🎉</p>}
                  {analysis.issues.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="text-sm">{it.topic}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {it.mentions != null && <span className="text-[11px] text-muted-foreground">×{it.mentions}</span>}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border capitalize ${sevColor[it.severity] || sevColor.low}`}>{it.severity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-md border border-border bg-card p-4">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-success mb-2.5"><Smiley size={16} weight="fill" /> Positive remarks</div>
                <div className="space-y-2">
                  {analysis.positives.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2">
                      <span className="text-sm">{p.topic}</span>
                      {p.mentions != null && <span className="text-[11px] text-muted-foreground shrink-0">×{p.mentions}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {analysis.recommendation && (
              <div className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 p-3.5">
                <TrendUp size={17} weight="fill" className="text-primary shrink-0 mt-0.5" />
                <div><span className="text-[11px] uppercase tracking-wide font-semibold text-primary">Recommendation</span><p className="text-sm mt-0.5">{analysis.recommendation}</p></div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* raw responses */}
      <div className="rounded-lg border border-border bg-card mt-4 overflow-hidden">
        <div className="px-5 py-3 border-b border-border text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">Responses · {data?.count ?? 0}</div>
        <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
          {isLoading && <div className="px-5 py-8 text-center text-muted-foreground text-sm">Loading…</div>}
          {(data?.responses || []).map((r, i) => (
            <div key={r.id} data-testid={`survey-row-${i}`} className="flex items-start gap-3 px-5 py-3">
              <span className={`font-heading text-lg font-semibold w-8 text-center ${scoreColor(r.score)}`}>{r.score}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm">{r.comment}</p>
                <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                  <span>{r.guest_name}</span>
                  <span className={`px-1.5 py-0.5 rounded uppercase text-[10px] ${chanColor[r.channel] || chanColor.survey}`}>{r.channel}</span>
                  <span>{timeAgo(r.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}

function Stat({ icon: Icon, label, value, hint, accent }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 fade-up">
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{label}</span>
        <span className={`h-8 w-8 rounded-md flex items-center justify-center ${accent ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}><Icon size={16} weight="fill" /></span>
      </div>
      <div className="mt-3 font-heading text-[28px] leading-none font-semibold">{value}</div>
      <div className="mt-2 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}
